import {
  slugify,
  textToHtml,
  type ActivityDetail,
  type CreateActivityRequest,
} from '@utazzo/shared';
import {
  UzAlert,
  UzButton,
  UzDateTimePicker,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
  UzTabs,
  UzTextarea,
  UzTooltip,
} from '@utazzo/ui';
import { ArrowLeft, Plus, Sparkles, Trash2 } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, type UseFormReturn } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useActivityDetail,
  useApproveActivity,
  useCreateActivity,
  useUpdateActivity,
} from '../use-activities';

import { type GalleryPhoto } from '@/components/photo-gallery';
import {
  CityPicker,
  CountryPicker,
  CurrencyPicker,
  LanguagePicker,
  SupplierPicker,
} from '@/components/pickers';
import { CatalogEditorSkeleton } from '@/components/skeletons';
import { StarRatingInput } from '@/components/star-rating';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useCurrenciesList } from '@/features/currencies/use-currencies';
import { relativeTime } from '@/features/dashboard/dashboard-utils';
import { FixedMediaSlotsField } from '@/features/website-cms/components/FixedMediaSlotsField';
import { MediaDropField } from '@/features/website-cms/components/MediaDropField';
import { RichTextEditor } from '@/features/website-cms/components/RichTextEditor';
import { useListReturn } from '@/hooks/use-list-return';

/**
 * Full-page Activity edit (Phase B4.8.H).
 *
 * Replaces the prior drawer-based editor. Same 8-tab structure, served on
 * a wide canvas with a sticky save bar. Routes:
 *   /activities/new          mode=create
 *   /activities/:id/edit     mode=edit
 */

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const TRANSFER_TYPE_OPTIONS = [
  { value: 'SIC', label: 'SIC (shared)' },
  { value: 'PRIVATE', label: 'Private' },
];

const TRANSFER_DIRECTION_OPTIONS = [
  { value: 'PICKUP', label: 'Pickup included' },
  { value: 'DROPOFF', label: 'Drop-off included' },
  { value: 'BOTH', label: 'Both included' },
];

const MARKUP_OPTIONS = [
  { value: '', label: 'No markup' },
  { value: 'PERCENT', label: 'Percent' },
  { value: 'FLAT', label: 'Flat amount' },
];

const TIME_OF_DAY_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 'EARLY_MORNING', label: 'Early morning' },
  { value: 'MORNING', label: 'Morning' },
  { value: 'AFTERNOON', label: 'Afternoon' },
  { value: 'EVENING', label: 'Evening' },
  { value: 'NIGHT', label: 'Night' },
  { value: 'FULL_DAY', label: 'Full day' },
];

const WEEKDAY_OPTIONS: {
  value: 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';
  label: string;
}[] = [
  { value: 'MON', label: 'Mon' },
  { value: 'TUE', label: 'Tue' },
  { value: 'WED', label: 'Wed' },
  { value: 'THU', label: 'Thu' },
  { value: 'FRI', label: 'Fri' },
  { value: 'SAT', label: 'Sat' },
  { value: 'SUN', label: 'Sun' },
];

interface VariantFormRow {
  id?: string;
  name: string;
  shortDescription: string | null;
  longDescription: string | null;
  adultPrice: number;
  childPrice: number;
  /** Optional pax window. Empty = no gate. */
  minPax: number | null;
  maxPax: number | null;
  /** When > 0, priced as a flat lump-sum regardless of pax count in [min,max]. */
  fixedCost: number | null;
  transferIncluded: boolean;
  transferType: 'SIC' | 'PRIVATE' | null;
  transferDirection: 'PICKUP' | 'DROPOFF' | 'BOTH' | null;
}

const ALL_WEEKDAYS: Array<'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN'> = [
  'MON',
  'TUE',
  'WED',
  'THU',
  'FRI',
  'SAT',
  'SUN',
];

interface ActivityFormValues {
  status: ActivityDetail['status'];
  legacyCode: string | null;
  slug: string | null;
  name: string;
  category: string;
  subCategory: string | null;
  cityId: string | null;
  countryId: string | null;
  shortDescription: string | null;
  longDescriptionHtml: string | null;
  inclusionsHtml: string | null;
  exclusionsHtml: string | null;
  termsHtml: string | null;
  safetyHtml: string | null;
  cancellationHtml: string | null;
  vendorSupplierId: string | null;
  timeOfDayEnum: string[];
  languageId: string | null;
  pickupTime: string;
  scheduleStartTime: string;
  scheduleEndTime: string;
  pickupPoint: string;
  dropOffPoint: string;
  runOnWeekdays: Array<'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN'>;
  minimumEligibleAge: number;
  maximumEligibleAge: number;
  tourDurationMinutes: number;
  currencyId: string | null;
  // Ticket tiers - index 0 is the mandatory base "Entry Ticket".
  variants: VariantFormRow[];
  // Activity-level transfer pricing (0 = not offered).
  sicAdultPrice: number;
  sicChildPrice: number;
  privateUpto4: number;
  privateUpto7: number;
  privateUpto12: number;
  markupKind: 'PERCENT' | 'FLAT' | null;
  markupValue: number;
  validityFrom: string | null;
  validityTo: string | null;
  travelStartDate: string | null;
  travelEndDate: string | null;
  imageUrl: string | null;
  photos: GalleryPhoto[];
  // Standalone 4-image gallery (admin-only), separate from `photos`.
  galleryImages: string[];
  rating: number;
  metaTagName: string | null;
  additionalDetails: string | null;
}

const EMPTY_BASE_VARIANT: VariantFormRow = {
  name: '',
  shortDescription: null,
  longDescription: null,
  adultPrice: 0,
  childPrice: 0,
  minPax: null,
  maxPax: null,
  fixedCost: null,
  transferIncluded: false,
  transferType: null,
  transferDirection: null,
};

const CREATE_DEFAULTS: ActivityFormValues = {
  status: 'DRAFT',
  legacyCode: null,
  slug: null,
  name: '',
  category: 'Activity',
  subCategory: null,
  cityId: null,
  countryId: null,
  shortDescription: null,
  longDescriptionHtml: null,
  inclusionsHtml: null,
  exclusionsHtml: null,
  termsHtml: null,
  safetyHtml: null,
  cancellationHtml: null,
  vendorSupplierId: null,
  timeOfDayEnum: [],
  languageId: null,
  pickupTime: '',
  scheduleStartTime: '',
  scheduleEndTime: '',
  pickupPoint: '',
  dropOffPoint: '',
  runOnWeekdays: [...ALL_WEEKDAYS],
  minimumEligibleAge: 3,
  maximumEligibleAge: 120,
  tourDurationMinutes: 0,
  currencyId: null,
  variants: [{ ...EMPTY_BASE_VARIANT }],
  sicAdultPrice: 0,
  sicChildPrice: 0,
  privateUpto4: 0,
  privateUpto7: 0,
  privateUpto12: 0,
  markupKind: null,
  markupValue: 0,
  validityFrom: null,
  validityTo: null,
  travelStartDate: null,
  travelEndDate: null,
  imageUrl: null,
  photos: [],
  galleryImages: [],
  rating: 0,
  metaTagName: null,
  additionalDetails: null,
};

const parsePhotos = (a: unknown): GalleryPhoto[] => {
  if (!Array.isArray(a)) return [];
  return a.filter(
    (p): p is GalleryPhoto =>
      Boolean(p) &&
      typeof p === 'object' &&
      (typeof (p as { url?: unknown }).url === 'string' ||
        typeof (p as { mediaId?: unknown }).mediaId === 'string'),
  );
};

const trimOrNull = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length === 0 ? null : t;
};

const toFormValues = (activity: ActivityDetail | null): ActivityFormValues => {
  if (!activity) return { ...CREATE_DEFAULTS };
  // Base variant first, then the rest by sortOrder. Always present (>=1).
  const sortedVariants = [...activity.variants].sort(
    (a, b) => Number(b.isBase) - Number(a.isBase) || a.sortOrder - b.sortOrder,
  );
  const variants: VariantFormRow[] =
    sortedVariants.length > 0
      ? sortedVariants.map((v) => ({
          id: v.id,
          name: v.name,
          shortDescription: v.shortDescription,
          longDescription: v.longDescription,
          adultPrice: v.adultPrice,
          childPrice: v.childPrice,
          minPax: v.minPax,
          maxPax: v.maxPax,
          fixedCost: v.fixedCost,
          transferIncluded: v.transferIncluded,
          transferType: v.transferType,
          transferDirection: v.transferDirection,
        }))
      : [{ ...EMPTY_BASE_VARIANT }];
  return {
    status: activity.status,
    legacyCode: activity.legacyCode,
    slug: activity.slug,
    name: activity.name,
    category: activity.category,
    subCategory: activity.subCategory,
    cityId: activity.cityId,
    countryId: activity.countryId,
    shortDescription: activity.shortDescription,
    longDescriptionHtml: activity.longDescriptionHtml,
    inclusionsHtml: activity.inclusionsHtml,
    exclusionsHtml: activity.exclusionsHtml,
    termsHtml: activity.termsHtml,
    safetyHtml: activity.safetyHtml,
    cancellationHtml: activity.cancellationHtml,
    vendorSupplierId: activity.vendorSupplierId,
    timeOfDayEnum: activity.timeOfDayEnum,
    languageId: activity.languageId,
    pickupTime: activity.pickupTime ?? '',
    scheduleStartTime: activity.scheduleStartTime ?? '',
    scheduleEndTime: activity.scheduleEndTime ?? '',
    pickupPoint: activity.pickupPoint ?? '',
    dropOffPoint: activity.dropOffPoint ?? '',
    runOnWeekdays: activity.runOnWeekdays,
    minimumEligibleAge: activity.minimumEligibleAge,
    maximumEligibleAge: activity.maximumEligibleAge,
    tourDurationMinutes: activity.tourDurationMinutes,
    currencyId: activity.currencyId,
    variants,
    sicAdultPrice: activity.sicAdultPrice ?? 0,
    sicChildPrice: activity.sicChildPrice ?? 0,
    privateUpto4: activity.privateUpto4 ?? 0,
    privateUpto7: activity.privateUpto7 ?? 0,
    privateUpto12: activity.privateUpto12 ?? 0,
    markupKind: activity.markupKind,
    markupValue: activity.markupValue,
    validityFrom: activity.validityFrom,
    validityTo: activity.validityTo,
    travelStartDate: activity.travelStartDate,
    travelEndDate: activity.travelEndDate,
    imageUrl: activity.imageUrl,
    photos: parsePhotos(activity.photos),
    galleryImages: Array.isArray(activity.galleryImageMediaIds)
      ? activity.galleryImageMediaIds
      : [],
    rating: activity.rating,
    metaTagName: activity.metaTagName,
    additionalDetails: activity.additionalDetails,
  };
};

const posOrNull = (n: number): number | null => (n > 0 ? n : null);

/**
 * Keep only the keys whose value actually changed vs the original (JSON-
 * compared, so arrays/objects like photos and variants are handled too). Used
 * so the edit form PATCHes just the fields the user touched: an image-only edit
 * then sends only `photos`, which (per the catalog approval split) publishes
 * live without spawning a no-op pending-approval entry for unchanged fields.
 */
const pickChanged = <T extends Record<string, unknown>>(next: T, prev: T): T => {
  const out = {} as T;
  for (const key of Object.keys(next) as (keyof T)[]) {
    if (JSON.stringify(next[key]) !== JSON.stringify(prev[key])) out[key] = next[key];
  }
  return out;
};

const fromFormValues = (values: ActivityFormValues): CreateActivityRequest => ({
  status: values.status,
  legacyCode: trimOrNull(values.legacyCode),
  slug: trimOrNull(values.slug),
  name: values.name.trim(),
  category: values.category,
  subCategory: trimOrNull(values.subCategory),
  cityId: values.cityId ?? null,
  countryId: values.countryId ?? null,
  shortDescription: trimOrNull(values.shortDescription),
  longDescriptionHtml: trimOrNull(values.longDescriptionHtml),
  inclusionsHtml: trimOrNull(values.inclusionsHtml),
  exclusionsHtml: trimOrNull(values.exclusionsHtml),
  termsHtml: trimOrNull(values.termsHtml),
  safetyHtml: trimOrNull(values.safetyHtml),
  cancellationHtml: trimOrNull(values.cancellationHtml),
  vendorSupplierId: values.vendorSupplierId ?? null,
  timeOfDayEnum: values.timeOfDayEnum as CreateActivityRequest['timeOfDayEnum'],
  languageId: values.languageId ?? null,
  pickupTime: values.pickupTime.trim() || null,
  scheduleStartTime: values.scheduleStartTime.trim() || null,
  scheduleEndTime: values.scheduleEndTime.trim() || null,
  pickupPoint: values.pickupPoint.trim() || null,
  dropOffPoint: values.dropOffPoint.trim() || null,
  runOnWeekdays: values.runOnWeekdays,
  minimumEligibleAge: values.minimumEligibleAge,
  maximumEligibleAge: values.maximumEligibleAge,
  tourDurationMinutes: values.tourDurationMinutes,
  currencyId: values.currencyId ?? null,
  variants: values.variants.map((v, i) => ({
    id: v.id,
    name: v.name.trim() || 'Entry Ticket',
    shortDescription: trimOrNull(v.shortDescription),
    longDescription: trimOrNull(v.longDescription),
    adultPrice: v.adultPrice || 0,
    childPrice: v.childPrice || 0,
    minPax: v.minPax && v.minPax > 0 ? v.minPax : null,
    maxPax: v.maxPax && v.maxPax > 0 ? v.maxPax : null,
    fixedCost: v.fixedCost && v.fixedCost > 0 ? v.fixedCost : null,
    isBase: i === 0,
    transferIncluded: v.transferIncluded,
    transferType: v.transferIncluded ? v.transferType : null,
    transferDirection: v.transferIncluded ? (v.transferDirection ?? 'BOTH') : null,
    sortOrder: i,
  })),
  sicAdultPrice: posOrNull(values.sicAdultPrice),
  sicChildPrice: posOrNull(values.sicChildPrice),
  privateUpto4: posOrNull(values.privateUpto4),
  privateUpto7: posOrNull(values.privateUpto7),
  privateUpto12: posOrNull(values.privateUpto12),
  markupKind: values.markupKind,
  markupValue: values.markupValue,
  validityFrom: values.validityFrom || null,
  validityTo: values.validityTo || null,
  travelStartDate: values.travelStartDate || null,
  travelEndDate: values.travelEndDate || null,
  imageUrl: trimOrNull(values.imageUrl),
  photos: values.photos.length > 0 ? values.photos : null,
  galleryImageMediaIds: values.galleryImages,
  rating: values.rating,
  metaTagName: trimOrNull(values.metaTagName),
  additionalDetails: trimOrNull(values.additionalDetails),
});

/** Shopify-style card: title header + stacked fields. */
const Card = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}): React.JSX.Element => (
  <section className="rounded-lg border border-zinc-200 bg-white">
    <header className="border-b border-zinc-100 px-5 py-3.5">
      <h3 className="text-sm font-semibold tracking-tight text-zinc-900">{title}</h3>
      {description && <p className="mt-0.5 text-xs text-zinc-500">{description}</p>}
    </header>
    <div className="space-y-5 px-5 py-5">{children}</div>
  </section>
);

const WEEKDAY_TOGGLE: {
  value: 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';
  label: string;
}[] = WEEKDAY_OPTIONS;

export const ActivityEditPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Returns to the list as the operator left it (page size, page, search,
  // filters, sort) instead of a bare /activities that resets all of them.
  const backToList = useListReturn('/activities');
  const me = useCurrentUser();
  const canApprove = me?.role === 'ADMIN';
  const isCreate = !id || id === 'new';
  const [activeTab, setActiveTab] = useState<string>('details');

  const detailQuery = useActivityDetail(isCreate ? null : (id ?? null));
  const activity = detailQuery.data ?? null;

  const createActivity = useCreateActivity();
  const updateActivity = useUpdateActivity();
  const approveActivity = useApproveActivity();
  const pending = createActivity.isPending || updateActivity.isPending || approveActivity.isPending;

  const form = useForm<ActivityFormValues>({ defaultValues: toFormValues(null) });

  const currenciesQuery = useCurrenciesList({ page: 1, pageSize: 200, sortDir: 'asc' });

  useEffect(() => {
    if (isCreate) form.reset(toFormValues(null));
    else if (activity) form.reset(toFormValues(activity));
  }, [isCreate, activity, form]);

  // Default the currency to INR on a fresh activity once the list loads.
  useEffect(() => {
    if (!isCreate) return;
    if (form.getValues('currencyId')) return;
    const inr = currenciesQuery.data?.data.find((c) => c.code === 'INR');
    if (inr) form.setValue('currencyId', inr.id);
  }, [isCreate, currenciesQuery.data, form]);

  const submit = form.handleSubmit(async (values) => {
    if (!values.slug || !values.slug.trim()) {
      form.setError('slug', { message: 'URL slug is required (use the wand to generate)' });
      setActiveTab('details');
      return;
    }
    if (!values.countryId) {
      form.setError('countryId', { message: 'Country is required' });
      setActiveTab('details');
      return;
    }
    if (!values.cityId) {
      form.setError('cityId', { message: 'City is required' });
      setActiveTab('details');
      return;
    }
    // Cover photo (photos sortOrder 0) is required.
    const cover = values.photos.find((p) => (p.sortOrder ?? 0) === 0);
    if (!cover || !(cover.mediaId || cover.url)) {
      form.setError('photos', { message: 'Cover photo is required.' });
      setActiveTab('content');
      return;
    }
    // All 4 standalone gallery images are required.
    if ((values.galleryImages?.length ?? 0) < 4) {
      form.setError('galleryImages', { message: 'All 4 gallery images are required.' });
      setActiveTab('content');
      return;
    }
    const payload = fromFormValues(values);
    if (isCreate) {
      const created = await createActivity.mutateAsync(payload);
      navigate(`/activities/${created.id}/edit`);
    } else if (activity) {
      // PATCH only the fields the user actually changed (diff vs the loaded
      // activity, mapped through the same fromFormValues so types line up).
      const diff = pickChanged(payload, fromFormValues(toFormValues(activity)));
      await updateActivity.mutateAsync({ id: activity.id, payload: diff });
    }
  });

  const tabs = useMemo(() => {
    const list: { value: string; label: ReactNode; content: ReactNode }[] = [
      {
        value: 'details',
        label: 'Details',
        content: (
          <UzForm form={form} onSubmit={submit}>
            <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
              <div className="min-w-0 space-y-6">
                <IdentityCard form={form} pending={pending} />
                <LocationCard form={form} pending={pending} />
                <VariantsCard form={form} pending={pending} />
                <TransferPricingCard form={form} pending={pending} />
                <ScheduleCard pending={pending} />
              </div>
              <aside className="space-y-6 self-start lg:sticky lg:top-8">
                <StatusCard
                  pending={pending}
                  activity={activity}
                  canApprove={canApprove}
                  onToggleApprove={(tier, approve) =>
                    activity &&
                    approveActivity.mutate({ id: activity.id, payload: { tier, approve } })
                  }
                />
                <CommercialCard pending={pending} />
                <LogisticsCard pending={pending} />
              </aside>
            </div>
          </UzForm>
        ),
      },
      {
        value: 'content',
        label: 'Content & media',
        content: (
          <UzForm form={form} onSubmit={submit}>
            <div className="mx-auto max-w-3xl space-y-6">
              <ContentCard pending={pending} />
              <MediaCard pending={pending} />
              <GalleryCard pending={pending} />
            </div>
          </UzForm>
        ),
      },
    ];
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- submit stable per render
  }, [isCreate, activity, canApprove, form, pending, approveActivity]);

  const statusDot =
    activity?.status === 'ACTIVE'
      ? 'bg-emerald-500'
      : activity?.status === 'DRAFT' || activity?.status === 'PENDING_APPROVAL'
        ? 'bg-amber-500'
        : 'bg-zinc-400';
  const statusText =
    activity?.status === 'ACTIVE'
      ? 'Active'
      : activity?.status === 'DRAFT'
        ? 'Draft'
        : activity?.status === 'PENDING_APPROVAL'
          ? 'Pending approval'
          : activity?.status === 'INACTIVE'
            ? 'Inactive'
            : activity?.status === 'ARCHIVED'
              ? 'Archived'
              : '';

  return (
    <div className="-mx-4 -mt-6 flex min-h-full flex-col lg:-mx-8 lg:-mt-8">
      {/* Sticky top bar - breaks out of the scroll container's padding so it sits
          flush under the app header and stays solid (content never shows through). */}
      <div className="sticky -top-6 z-30 flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-6 lg:-top-8 lg:px-8">
        <button
          type="button"
          onClick={backToList}
          className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          aria-label="Back to activities"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">Activities</span>
          <span className="text-zinc-300" aria-hidden>
            /
          </span>
          <span className="truncate text-sm font-semibold tracking-tight text-zinc-900">
            {isCreate ? 'Add activity' : activity ? activity.name : 'Edit'}
          </span>
        </div>
        <span className="flex-1" />
        {!isCreate && activity && (
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
            <span className={'h-1.5 w-1.5 rounded-full ' + statusDot} aria-hidden />
            {statusText}
          </span>
        )}
        <UzButton variant="ghost" size="sm" onClick={backToList} disabled={pending}>
          Cancel
        </UzButton>
        <UzButton size="sm" onClick={submit} loading={pending}>
          <span>{isCreate ? 'Create' : 'Save'}</span>
          <kbd className="ml-2 hidden h-4 items-center rounded border border-zinc-300/60 bg-white/15 px-1 font-mono text-[10px] text-current/80 sm:inline-flex">
            ⌘S
          </kbd>
        </UzButton>
      </div>

      <div className="mx-auto w-full max-w-6xl min-w-0 px-6 py-6 lg:px-8">
        {!isCreate && activity && (
          <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
            {activity.isApprovedByAdmin && (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <span className="h-1 w-1 rounded-full bg-emerald-500" aria-hidden />
                Admin approved
              </span>
            )}
            {activity.isApprovedByModerator && <span>Moderator approved</span>}
            {activity.legacyId !== null && (
              <span>
                Legacy{' '}
                <span className="font-mono text-zinc-700 tabular-nums">#{activity.legacyId}</span>
              </span>
            )}
            {activity.legacyCode && (
              <span>
                Code <span className="font-mono text-zinc-700">{activity.legacyCode}</span>
              </span>
            )}
          </div>
        )}

        {!isCreate && detailQuery.isLoading && <CatalogEditorSkeleton />}
        {!isCreate && detailQuery.isError && (
          <UzAlert tone="destructive">Failed to load activity detail. Go back and retry.</UzAlert>
        )}

        {/* The fields below show the LAST APPROVED version, not the captured
            edit - without this banner the form looks like the save was lost. */}
        {activity?.awaitingApproval && (
          <UzAlert tone="warning">
            <span className="font-medium">Waiting on an admin.</span> Your changes are saved and
            queued for approval
            {activity.pendingSubmittedAt ? ` (${relativeTime(activity.pendingSubmittedAt)})` : ''}.
            The fields below still show the live version until an admin approves.
          </UzAlert>
        )}

        {(isCreate || activity) && (
          <UzTabs value={activeTab} onValueChange={setActiveTab} tabs={tabs} />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

interface CardProps {
  pending: boolean;
}
interface FormCardProps extends CardProps {
  form: UseFormReturn<ActivityFormValues>;
}

const IdentityCard = ({ form, pending }: FormCardProps): React.JSX.Element => (
  <Card title="Identity">
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Activity name"
          placeholder="e.g. Burj Khalifa entry ticket"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <div className="grid gap-5 sm:grid-cols-2">
      <UzFormField name="slug">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="URL slug"
            required
            error={error}
            disabled={pending}
            rightAdornment={
              <UzTooltip label="Auto-generate slug">
                <button
                  type="button"
                  disabled={pending}
                  aria-label="Auto-generate slug"
                  onClick={() => {
                    const name = (form.getValues('name') ?? '').trim();
                    if (name) field.onChange(slugify(name));
                  }}
                  className="text-zinc-400 transition-colors hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Sparkles className="h-4 w-4" />
                </button>
              </UzTooltip>
            }
          />
        )}
      </UzFormField>
      <UzFormField name="subCategory">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Tags (optional)"
            placeholder="Sightseeing, Adventure, Theme Park, ..."
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
    <UzFormField name="rating">
      {({ field }) => (
        <StarRatingInput
          label="Rating"
          value={(field.value as number) ?? 0}
          onChange={(v) => field.onChange(v)}
          disabled={pending}
        />
      )}
    </UzFormField>
  </Card>
);

const LocationCard = ({ form, pending }: FormCardProps): React.JSX.Element => {
  const countryId = form.watch('countryId');
  return (
    <Card title="Location">
      <div className="grid gap-5 sm:grid-cols-2">
        <UzFormField name="countryId">
          {({ field, error }) => (
            <CountryPicker
              value={(field.value as string | null | undefined) ?? null}
              onValueChange={(v) => {
                field.onChange(v);
                if (v !== countryId) form.setValue('cityId', null);
              }}
              required
              disabled={pending}
              error={error}
            />
          )}
        </UzFormField>
        <UzFormField name="cityId">
          {({ field, error }) => (
            <CityPicker
              value={(field.value as string | null | undefined) ?? null}
              onValueChange={(v) => field.onChange(v)}
              countryId={countryId ?? null}
              required
              disabled={pending}
              error={error}
            />
          )}
        </UzFormField>
      </div>
    </Card>
  );
};

const VariantRow = ({
  form,
  index,
  isBase,
  pending,
  onRemove,
}: {
  form: UseFormReturn<ActivityFormValues>;
  index: number;
  isBase: boolean;
  pending: boolean;
  onRemove: () => void;
}): React.JSX.Element => {
  const included = form.watch(`variants.${index}.transferIncluded`);
  return (
    <div className="space-y-4 rounded-md border border-zinc-200 p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
          {isBase ? 'Base ticket (Entry Ticket)' : `Variant ${index + 1}`}
        </span>
        {!isBase && (
          <button
            type="button"
            onClick={onRemove}
            disabled={pending}
            aria-label="Remove variant"
            className="text-zinc-400 transition-colors hover:text-red-600 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      <UzFormField name={`variants.${index}.name`}>
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Variant name"
            placeholder={isBase ? 'Entry Ticket' : 'e.g. VIP'}
            required
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name={`variants.${index}.shortDescription`}>
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Short description"
            placeholder="One-sentence summary for picker cards."
            rows={2}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      {/* Rich text: shown under the day's photos on the customer share page.
          Older rows hold plain text, lifted into paragraphs for the editor. */}
      <UzFormField name={`variants.${index}.longDescription`}>
        {({ field }) => (
          <RichTextEditor
            label="Long description"
            placeholder="Multi-paragraph description with formatting..."
            value={textToHtml(field.value as string | null | undefined)}
            onChange={(html) => field.onChange(html === '' ? null : html)}
            disabled={pending}
            folder="activities"
            size="compact"
          />
        )}
      </UzFormField>
      <div className="grid gap-5 sm:grid-cols-2">
        <MoneyField name={`variants.${index}.adultPrice`} label="Adult price" pending={pending} />
        <MoneyField name={`variants.${index}.childPrice`} label="Child price" pending={pending} />
      </div>
      <div className="grid gap-5 sm:grid-cols-3">
        <UzFormField name={`variants.${index}.minPax`}>
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              label="Min pax"
              precision={0}
              min={1}
              value={(field.value as number | null | undefined) ?? ''}
              onValueChange={(v) => field.onChange(v ?? null)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name={`variants.${index}.maxPax`}>
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              label="Max pax"
              precision={0}
              min={1}
              value={(field.value as number | null | undefined) ?? ''}
              onValueChange={(v) => field.onChange(v ?? null)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <MoneyField
          name={`variants.${index}.fixedCost`}
          label="Fixed cost"
          hint="When set, priced flat for any pax count in [min, max]. Overrides adult/child price."
          pending={pending}
        />
      </div>
      <UzFormField name={`variants.${index}.transferIncluded`}>
        {({ field }) => (
          <UzSwitch
            label="Transfer included"
            description="Bundle a transfer; the cost comes from the transfer pricing below."
            checked={Boolean(field.value)}
            onCheckedChange={(c) => {
              const on = Boolean(c);
              field.onChange(on);
              if (on) {
                if (!form.getValues(`variants.${index}.transferType`)) {
                  form.setValue(`variants.${index}.transferType`, 'SIC');
                }
                if (!form.getValues(`variants.${index}.transferDirection`)) {
                  form.setValue(`variants.${index}.transferDirection`, 'BOTH');
                }
              } else {
                form.setValue(`variants.${index}.transferType`, null);
                form.setValue(`variants.${index}.transferDirection`, null);
              }
            }}
            disabled={pending}
          />
        )}
      </UzFormField>
      {included && (
        <div className="grid gap-5 sm:grid-cols-2">
          <UzFormField name={`variants.${index}.transferType`}>
            {({ field, error }) => (
              <UzSelect
                name={field.name}
                label="Transfer type"
                options={TRANSFER_TYPE_OPTIONS}
                value={(field.value as string | null | undefined) ?? ''}
                onValueChange={(v) => field.onChange(v ? (v as 'SIC' | 'PRIVATE') : null)}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
          <UzFormField name={`variants.${index}.transferDirection`}>
            {({ field, error }) => (
              <UzSelect
                name={field.name}
                label="Transfer covers"
                options={TRANSFER_DIRECTION_OPTIONS}
                value={(field.value as string | null | undefined) ?? 'BOTH'}
                onValueChange={(v) =>
                  field.onChange((v as 'PICKUP' | 'DROPOFF' | 'BOTH') || 'BOTH')
                }
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
        </div>
      )}
    </div>
  );
};

const VariantsCard = ({ form, pending }: FormCardProps): React.JSX.Element => {
  const markupKind = form.watch('markupKind');
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'variants' });
  return (
    <Card
      title="Ticket variants"
      description="Per-pax prices in the selected currency (no infant). The first row is the mandatory base entry ticket."
    >
      <UzFormField name="currencyId">
        {({ field, error }) => (
          <CurrencyPicker
            value={(field.value as string | null | undefined) ?? null}
            onValueChange={(v) => field.onChange(v)}
            disabled={pending}
            error={error}
          />
        )}
      </UzFormField>
      <div className="space-y-4">
        {fields.map((f, index) => (
          <VariantRow
            key={f.id}
            form={form}
            index={index}
            isBase={index === 0}
            pending={pending}
            onRemove={() => remove(index)}
          />
        ))}
      </div>
      <div>
        <UzButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => append({ ...EMPTY_BASE_VARIANT })}
          disabled={pending}
        >
          <Plus className="h-4 w-4" />
          <span>Add variant</span>
        </UzButton>
      </div>
      <div className="grid gap-5 border-t border-zinc-100 pt-5 sm:grid-cols-2">
        <UzFormField name="markupKind">
          {({ field, error }) => (
            <UzSelect
              name={field.name}
              label="Markup type"
              options={MARKUP_OPTIONS}
              value={(field.value as string | null | undefined) ?? ''}
              onValueChange={(v) => field.onChange(v ? (v as 'PERCENT' | 'FLAT') : null)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        {markupKind && (
          <UzFormField name="markupValue">
            {({ field, error }) => (
              <UzNumberInput
                name={field.name}
                label={markupKind === 'PERCENT' ? 'Markup %' : 'Markup amount'}
                precision={2}
                min={0}
                rightAdornment={
                  markupKind === 'PERCENT' ? (
                    <span className="text-sm text-zinc-500">%</span>
                  ) : undefined
                }
                value={(field.value as number | undefined) || ''}
                onValueChange={(v) => field.onChange(v ?? 0)}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
        )}
      </div>
    </Card>
  );
};

const TransferPricingCard = ({ pending }: FormCardProps): React.JSX.Element => (
  <Card
    title="Transfer pricing"
    description="Optional. Configure once; a variant opts in via 'Transfer included'. Leave 0 if not offered."
  >
    <div>
      <p className="mb-3 text-xs font-medium tracking-wide text-zinc-500 uppercase">
        SIC (shared) - per pax
      </p>
      <div className="grid gap-5 sm:grid-cols-2">
        <MoneyField name="sicAdultPrice" label="Adult" pending={pending} />
        <MoneyField name="sicChildPrice" label="Child" pending={pending} />
      </div>
    </div>
    <div>
      <p className="mb-3 text-xs font-medium tracking-wide text-zinc-500 uppercase">
        Private - flat cost per group size
      </p>
      <div className="grid gap-5 sm:grid-cols-3">
        <MoneyField name="privateUpto4" label="Up to 4 pax" pending={pending} />
        <MoneyField name="privateUpto7" label="Up to 7 pax" pending={pending} />
        <MoneyField name="privateUpto12" label="Up to 12 pax" pending={pending} />
      </div>
    </div>
  </Card>
);

const ScheduleCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Schedule & capacity">
    <UzFormField name="timeOfDayEnum">
      {({ field }) => {
        const current = (field.value as string[] | undefined) ?? [];
        const toggle = (v: string): void => {
          if (current.includes(v)) field.onChange(current.filter((x) => x !== v));
          else field.onChange([...current, v]);
        };
        return (
          <div>
            <span className="mb-2 block text-sm leading-none font-medium text-zinc-900">
              Time of day
            </span>
            <div className="flex flex-wrap gap-1.5">
              {TIME_OF_DAY_OPTIONS.filter((o) => o.value).map((o) => {
                const on = current.includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    disabled={pending}
                    onClick={() => toggle(o.value)}
                    className={
                      'h-8 rounded-md border px-3 text-xs font-medium transition-colors ' +
                      (on
                        ? 'border-zinc-900 bg-zinc-900 text-white'
                        : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50')
                    }
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-zinc-500">
              Select one or more slots when the activity runs.
            </p>
          </div>
        );
      }}
    </UzFormField>
    <div className="grid gap-5 sm:grid-cols-2">
      <UzFormField name="tourDurationMinutes">
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Duration (minutes)"
            precision={0}
            min={0}
            value={field.value === null || field.value === undefined ? 0 : (field.value as number)}
            onValueChange={(v) => field.onChange(v ?? 0)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
    {/* Schedule clock-times (legacy). End auto-fills from start + duration on import; editable. */}
    <div className="grid gap-5 sm:grid-cols-3">
      <UzFormField name="pickupTime">
        {({ field, error }) => (
          <UzDateTimePicker
            mode="time"
            label="Pickup time"
            value={hhmmToDate(field.value as string | null | undefined)}
            onValueChange={(d) => field.onChange(dateToHhmm(d) || null)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="scheduleStartTime">
        {({ field, error }) => (
          <UzDateTimePicker
            mode="time"
            label="Start time"
            value={hhmmToDate(field.value as string | null | undefined)}
            onValueChange={(d) => field.onChange(dateToHhmm(d) || null)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="scheduleEndTime">
        {({ field, error }) => (
          <UzDateTimePicker
            mode="time"
            label="End time"
            value={hhmmToDate(field.value as string | null | undefined)}
            onValueChange={(d) => field.onChange(dateToHhmm(d) || null)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
    <WeekdaysToggle pending={pending} />
    <div className="grid gap-5 sm:grid-cols-2">
      <UzFormField name="minimumEligibleAge">
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Min age"
            precision={0}
            min={0}
            value={field.value === null || field.value === undefined ? 0 : (field.value as number)}
            onValueChange={(v) => field.onChange(v ?? 0)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="maximumEligibleAge">
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Max age"
            precision={0}
            min={0}
            value={
              field.value === null || field.value === undefined ? 120 : (field.value as number)
            }
            onValueChange={(v) => field.onChange(v ?? 120)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
    <div className="grid gap-5 sm:grid-cols-2">
      <UzFormField name="validityFrom">
        {({ field, error }) => (
          <UzDateTimePicker
            mode="date"
            displayFormat="dd/MM/yyyy"
            placeholder="dd/mm/yyyy"
            label="Valid from"
            value={ymdToDate(field.value as string | null | undefined)}
            onValueChange={(d) => field.onChange(dateToYmd(d) || null)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="validityTo">
        {({ field, error }) => (
          <UzDateTimePicker
            mode="date"
            displayFormat="dd/MM/yyyy"
            placeholder="dd/mm/yyyy"
            label="Valid to"
            value={ymdToDate(field.value as string | null | undefined)}
            onValueChange={(d) => field.onChange(dateToYmd(d) || null)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
  </Card>
);

const WEEKDAY_PRESETS: {
  label: string;
  days: Array<'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN'>;
}[] = [
  { label: 'All days', days: ALL_WEEKDAYS },
  { label: 'Weekdays', days: ['MON', 'TUE', 'WED', 'THU', 'FRI'] },
  { label: 'Weekends', days: ['SAT', 'SUN'] },
];

const WeekdaysToggle = ({ pending }: CardProps): React.JSX.Element => (
  <UzFormField name="runOnWeekdays">
    {({ field }) => {
      const current =
        (field.value as Array<'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN'> | undefined) ??
        [];
      const key = (arr: ReadonlyArray<string>): string => [...arr].sort().join(',');
      const toggle = (day: 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN') => {
        if (current.includes(day)) field.onChange(current.filter((d) => d !== day));
        else field.onChange([...current, day]);
      };
      return (
        <div>
          <span className="mb-2 block text-sm leading-none font-medium text-zinc-900">
            Operating days
          </span>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {WEEKDAY_PRESETS.map((p) => {
              const active = key(current) === key(p.days);
              return (
                <button
                  key={p.label}
                  type="button"
                  disabled={pending}
                  onClick={() => field.onChange([...p.days])}
                  className={
                    'h-8 rounded-md border px-3 text-xs font-medium transition-colors ' +
                    (active
                      ? 'border-zinc-900 bg-zinc-900 text-white'
                      : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50')
                  }
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAY_TOGGLE.map((d) => {
              const on = current.includes(d.value);
              return (
                <button
                  key={d.value}
                  type="button"
                  disabled={pending}
                  onClick={() => toggle(d.value)}
                  className={
                    'h-8 w-11 rounded-md border text-xs font-medium transition-colors ' +
                    (on
                      ? 'border-zinc-900 bg-zinc-900 text-white'
                      : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50')
                  }
                >
                  {d.label}
                </button>
              );
            })}
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            All days selected by default. Empty = available any day.
          </p>
        </div>
      );
    }}
  </UzFormField>
);

// ----- Sidebar cards -----

interface StatusCardProps extends CardProps {
  activity: ActivityDetail | null;
  canApprove: boolean;
  onToggleApprove: (tier: 'admin' | 'moderator', approve: boolean) => void;
}

const StatusCard = ({
  pending,
  activity,
  canApprove,
  onToggleApprove,
}: StatusCardProps): React.JSX.Element => (
  <Card title="Status">
    <UzFormField name="status">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Lifecycle"
          options={
            canApprove
              ? STATUS_OPTIONS
              : STATUS_OPTIONS.filter((o) => o.value === 'DRAFT' || o.value === 'PENDING_APPROVAL')
          }
          value={(field.value as string | undefined) || 'DRAFT'}
          onValueChange={(v) => field.onChange(v)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    {activity && canApprove && (
      <>
        <UzSwitch
          label={activity.isApprovedByAdmin ? 'Admin approved' : 'Admin not approved'}
          description="Admin approval is the gate to status=ACTIVE."
          checked={activity.isApprovedByAdmin}
          onCheckedChange={(checked) => onToggleApprove('admin', Boolean(checked))}
          disabled={pending}
        />
        <UzSwitch
          label={activity.isApprovedByModerator ? 'Moderator approved' : 'Moderator not approved'}
          description="Moderator sign-off is informational."
          checked={activity.isApprovedByModerator}
          onCheckedChange={(checked) => onToggleApprove('moderator', Boolean(checked))}
          disabled={pending}
        />
      </>
    )}
  </Card>
);

const CommercialCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Commercial">
    <UzFormField name="vendorSupplierId">
      {({ field, error }) => (
        <SupplierPicker
          value={(field.value as string | null | undefined) ?? null}
          onValueChange={(v) => field.onChange(v)}
          disabled={pending}
          error={error}
        />
      )}
    </UzFormField>
  </Card>
);

const LogisticsCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Logistics">
    {/* Pickup / drop-off are free text (legacy), one per row - no location master. */}
    <UzFormField name="pickupPoint">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Pickup point"
          placeholder="e.g. Hotel lobby"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="dropOffPoint">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Drop-off point"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="languageId">
      {({ field, error }) => (
        <LanguagePicker
          label="Guide language"
          value={(field.value as string | null | undefined) ?? null}
          onValueChange={(v) => field.onChange(v)}
          disabled={pending}
          error={error}
        />
      )}
    </UzFormField>
  </Card>
);

// ----- Content & media -----

const ContentCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Descriptions & policy">
    {/* 2026-07-09. Short + long description moved to the per-variant editor
        (Ticket variants card). The activity-level values are still loaded and
        saved untouched so existing data / website rendering is preserved -
        they just no longer have inputs here. */}
    <div className="grid gap-5 md:grid-cols-2">
      <UzFormField name="inclusionsHtml">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Inclusions"
            rows={6}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="exclusionsHtml">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Exclusions"
            rows={6}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="termsHtml">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Terms & conditions"
            rows={6}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="safetyHtml">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Safety instructions"
            rows={6}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
    <UzFormField name="cancellationHtml">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          label="Cancellation policy"
          rows={5}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="additionalDetails">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          label="Additional details"
          rows={3}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
  </Card>
);

const MediaCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Photos">
    {/* Cover only. The old multi-image "Gallery" was removed here in favour of
        the dedicated 4-box Gallery card below. We edit just the cover
        (photos sortOrder 0) and PRESERVE any other existing photos in the
        array so legacy gallery data is never dropped on save. */}
    <UzFormField name="photos">
      {({ field, error }) => {
        const photos = (field.value as GalleryPhoto[]) ?? [];
        const cover = photos.find((p) => (p.sortOrder ?? 0) === 0) ?? null;
        const setCover = (mediaId: string | null): void => {
          const rest = photos.filter((p) => (p.sortOrder ?? 0) !== 0);
          field.onChange(mediaId ? [{ mediaId, sortOrder: 0 }, ...rest] : rest);
        };
        return (
          <div className="space-y-1">
            <MediaDropField
              label="Cover photo"
              value={cover?.mediaId ?? null}
              onChange={setCover}
              folder="activities"
              variant="banner"
              disabled={pending}
            />
            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>
        );
      }}
    </UzFormField>
  </Card>
);

/**
 * Standalone gallery - a separate section of exactly 4 equal image slots
 * (no cover, no orientation). Always shows 4 boxes; drag a filled tile to
 * reorder; all 4 are required to save. Stored in the Activity's dedicated
 * `galleryImageMediaIds` field, independent of the Photos card above.
 */
const GalleryCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Gallery" description="Exactly 4 images. Drag a tile to reorder. All 4 are required.">
    <UzFormField name="galleryImages">
      {({ field, error }) => (
        <div className="space-y-1">
          <FixedMediaSlotsField
            value={(field.value as string[] | null | undefined) ?? []}
            onChange={(ids) => field.onChange(ids)}
            count={4}
            gridClassName="grid grid-cols-2 gap-3 sm:grid-cols-4"
            aspectClassName="aspect-square"
            folder="activities"
            disabled={pending}
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      )}
    </UzFormField>
  </Card>
);

/** Money input: rupees with 2 decimals, stored as a minor-unit string. */
/** "YYYY-MM-DD" -> local Date (midnight), for UzDateTimePicker. */
const ymdToDate = (s: string | null | undefined): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
/** Date -> "YYYY-MM-DD" (local). */
const dateToYmd = (d: Date | null): string =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : '';

/** "HH:MM" -> today's Date at that clock time, for UzDateTimePicker time mode. */
const hhmmToDate = (s: string | null | undefined): Date | null => {
  if (!s) return null;
  const [h, m] = s.split(':').map(Number);
  if (h === undefined || m === undefined || Number.isNaN(h) || Number.isNaN(m)) return null;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
};
/** Date -> "HH:MM" (local). */
const dateToHhmm = (d: Date | null): string =>
  d ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '';

const MoneyField = ({
  name,
  label,
  hint,
  pending,
}: {
  name: string;
  label: string;
  hint?: string;
  pending: boolean;
}): React.JSX.Element => (
  <UzFormField name={name}>
    {({ field, error }) => (
      <UzNumberInput
        name={field.name}
        label={label}
        precision={2}
        min={0}
        value={(field.value as number | undefined) || ''}
        onValueChange={(v) => field.onChange(v ?? 0)}
        hint={hint}
        error={error}
        disabled={pending}
      />
    )}
  </UzFormField>
);
